import * as React from 'react';
// mui
import {
  Breadcrumbs,
  Skeleton,
  Typography
} from '@mui/material';
// redux
import { useSelector } from 'react-redux';
import { selectAllTasks, selectTaskById } from '../../store/selectors/taskSelectors';
// types
import RecurringTask from '../../interfaces/RecurringTask.interface';
import Task from '../../interfaces/Task.interface';
import Event from '../../interfaces/Event.interface';
import { selectProjectById } from '../../store/selectors/projectSelectors';
import Project from '../../interfaces/Project.interface';

interface ItemBreadcrumbsProps {
  currentItem: Task | Event | RecurringTask;
  itemType: string;
  includeCurrentItem: boolean;
}

export default function ItemBreadcrumbs(props: ItemBreadcrumbsProps) {
  // props
  const currentItem: Task | Event | RecurringTask = props.currentItem;
  const itemType = props.itemType;
  const includeCurrentItem = props.includeCurrentItem;
  
  // redux
  const localTasks: Task[] = useSelector(selectAllTasks());
  const selectParentTask = currentItem.parentTaskId
    ? selectTaskById(currentItem.parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const localParentTask: Task | null = parentTaskRaw ?? null;
  const localProject: Project = useSelector(selectProjectById(currentItem.projectId)) as Project;

  // state
  const [ parentTaskNames, setParentTaskNames ] = React.useState<string[] | null>(null);

  const buildReverseTree = (tasks: Task[], currentTaskId: number, reverseTree: Task[] = []): Task[] => {
    if (tasks.length > 0) {
      for (let i = 0; i < tasks.length; i += 1) {
        if (tasks[i].subtaskIds.includes(currentTaskId)) {
          reverseTree.push(tasks[i]);
          return buildReverseTree(tasks, tasks[i].id, reverseTree);
        }
      }

      return reverseTree;
    } else {
      return [];
    }
  }

  const getParentTasksList = (reverseTree: Task[]): string[] => {
    return reverseTree.map((task: Task) => {
      return task.title;
    }).reverse();
  }

  React.useEffect(() => {
    if (currentItem.parentTaskId) {
      let reverseTree: Task[] = [];

      if (itemType === 'task') {
        reverseTree = buildReverseTree(localTasks, currentItem.id);
      } else {
        reverseTree = buildReverseTree(localTasks, currentItem.parentTaskId);
        if (localParentTask) {
          reverseTree.unshift(localParentTask);
        }
      }

      const parentNames = getParentTasksList(reverseTree);

      if (includeCurrentItem) {
        parentNames.push(currentItem.title);
      }

      setParentTaskNames(parentNames);
    } else {
      setParentTaskNames(includeCurrentItem ? [currentItem.title] : []); // no parents
    }
  }, [currentItem]);
  
  return (
    <React.Fragment>
      { parentTaskNames !== null ? (
        <Breadcrumbs>
          <Typography>{localProject.name}</Typography>
          { parentTaskNames.map((name: String, index: number) => 
            <Typography key={index} sx={{}}>{name}</Typography>
          )}
          { !includeCurrentItem && (
            <Typography> </Typography>
          )}
        </Breadcrumbs>
      ) : (
        <Breadcrumbs>
          <Skeleton variant="text" width={100} sx={{ fontSize: '1rem' }} />
          <Skeleton variant="text" width={80} sx={{ fontSize: '1rem' }} />
          <Skeleton variant="text" width={120} sx={{ fontSize: '1rem' }} />
        </Breadcrumbs>
      )}
    </React.Fragment>
  );
}
